// sapi-admin/src/components/GenerateProductQR.jsx
import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { Download, Printer, Copy, Check, X } from 'react-feather';
import { toast } from 'react-toastify';
import FormatPrice from '../../components/FormatPrice';

const GenerateProductQR = ({ product, onClose }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const generateQR = async () => {
      try {
        // URL de scan pour l'application frontend
        const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'https://sapi.etralis.com';
        const scanUrl = `${frontendUrl}/scan?product=${product.id}`;
        
        const qrImage = await QRCode.toDataURL(scanUrl, {
          width: 300,
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' }
        });
        
        setQrCodeUrl(qrImage);
      } catch (error) {
        console.error('Erreur génération QR:', error);
        toast.error('Erreur lors de la génération');
      } finally {
        setLoading(false);
      }
    };
    
    generateQR();
  }, [product]);

  const downloadQR = () => {
    const link = document.createElement('a');
    link.download = `QR_${product.name.replace(/\s/g, '_')}.png`;
    link.href = qrCodeUrl;
    link.click();
    toast.success('QR code téléchargé');
  };

  const printQR = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head><title>QR Code - ${product.name}</title></head>
        <body style="display:flex;justify-content:center;align-items:center;min-height:100vh;font-family:Arial;text-align:center;padding:20px;">
          <div>
            <img src="${qrCodeUrl}" style="width:250px;height:250px;" />
            <h3>${product.name}</h3>
            <p>Scannez ce QR code pour ajouter le produit à votre panier</p>
            <p style="font-size:12px;color:#666;">Prix: ${FormatPrice(product.price)}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.print();
    printWindow.close();
  };

  const copyLink = () => {
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'https://sapi.etralis.com';
    navigator.clipboard.writeText(`${frontendUrl}/product/${product.id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Lien copié !');
  };

  if (loading) {
    return (
      <div style={{
        background: "#0d1526",
        borderRadius: 16,
        padding: 32,
        textAlign: "center",
        border: "1px solid rgba(255,255,255,.1)",
        maxWidth: 400,
        width: "100%"
      }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          border: "2px solid rgba(45,212,191,.3)",
          borderTopColor: "#2dd4bf",
          animation: "spin .8s linear infinite",
          margin: "0 auto"
        }} />
        <p style={{ marginTop: 16, color: "#94a3b8", fontSize: 12 }}>Génération du QR code...</p>
      </div>
    );
  }

  return (
    <div style={{
      background: "#0d1526",
      borderRadius: 20,
      border: "1px solid rgba(255,255,255,.1)",
      maxWidth: 420,
      width: "100%",
      overflow: "hidden"
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 20px",
        borderBottom: "1px solid rgba(255,255,255,.06)",
        background: "rgba(45,212,191,.05)"
      }}>
        <div>
          <p style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".16em", color: "rgba(45,212,191,.7)", marginBottom: 3 }}>QR Code</p>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{product.name}</h3>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,.06)",
            border: "1px solid rgba(255,255,255,.08)",
            borderRadius: 8,
            padding: "6px 10px",
            cursor: "pointer",
            color: "#94a3b8",
            display: "flex"
          }}
        >
          <X size={14} />
        </button>
      </div>

      {/* QR Code Image */}
      <div style={{ padding: "24px", display: "flex", justifyContent: "center" }}>
        <div style={{
          background: "#fff",
          padding: 16,
          borderRadius: 16,
          boxShadow: "0 8px 24px rgba(0,0,0,.3)"
        }}>
          <img src={qrCodeUrl} alt="QR Code" style={{ width: 200, height: 200 }} />
        </div>
      </div>

      {/* Product Info */}
      <div style={{ padding: "0 20px", textAlign: "center" }}>
        <p style={{ fontSize: 20, fontWeight: 700, color: "#2dd4bf" }}>{FormatPrice(product.price)}</p>
        {product.stock > 0 ? (
          <p style={{ fontSize: 11, color: "#34d399", marginTop: 4 }}>✓ En stock ({product.stock})</p>
        ) : (
          <p style={{ fontSize: 11, color: "#f87171", marginTop: 4 }}>✗ Rupture de stock</p>
        )}
      </div>

      {/* Instructions */}
      <div style={{
        margin: "16px 20px",
        padding: "12px",
        background: "rgba(45,212,191,.08)",
        borderRadius: 12,
        border: "1px solid rgba(45,212,191,.15)"
      }}>
        <p style={{ fontSize: 11, color: "#2dd4bf", marginBottom: 4 }}>📱 Comment utiliser ?</p>
        <p style={{ fontSize: 10, color: "#94a3b8" }}>
          Scannez ce QR code avec l'application Sapi pour ajouter directement ce produit à votre panier.
        </p>
      </div>

      {/* Buttons */}
      <div style={{ padding: "20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <button
          onClick={downloadQR}
          style={{
            background: "rgba(45,212,191,.15)",
            border: "1px solid rgba(45,212,191,.3)",
            borderRadius: 10,
            padding: "10px",
            color: "#2dd4bf",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}
        >
          <Download size={14} /> Télécharger
        </button>
        <button
          onClick={printQR}
          style={{
            background: "rgba(255,255,255,.06)",
            border: "1px solid rgba(255,255,255,.08)",
            borderRadius: 10,
            padding: "10px",
            color: "#94a3b8",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}
        >
          <Printer size={14} /> Imprimer
        </button>
        <button
          onClick={copyLink}
          style={{
            background: "#1e293b",
            border: "none",
            borderRadius: 10,
            padding: "10px",
            color: "#cbd5e1",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            gridColumn: "span 2"
          }}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Lien copié !" : "Copier le lien du produit"}
        </button>
      </div>
    </div>
  );
};

export default GenerateProductQR;